import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Package, Clock, Plane, Ship, Truck, CheckCircle2, XCircle, Loader2, ArrowLeft, Copy, ExternalLink } from "lucide-react";
import { toast } from "sonner";
import { api, formatBRL } from "../api";

const STEPS = [
  { id: "Pendente", label: "Pendente", icon: Clock },
  { id: "Saiu da China", label: "Saiu da China", icon: Plane },
  { id: "Chegou ao Brasil", label: "Chegou ao Brasil", icon: Ship },
  { id: "Está no Correios", label: "Está no Correios", icon: Package },
  { id: "Saiu para Entrega", label: "Saiu para entrega", icon: Truck },
];

export default function OrderTracking() {
  const { id } = useParams();
  const [order, setOrder] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    let alive = true;
    const load = async () => {
      try {
        const { data } = await api.get(`/orders/track/${id}`);
        if (alive) setOrder(data);
      } catch {
        if (alive) setError("Pedido não encontrado. Confira o link do seu pedido.");
      }
    };
    load();
    const t = setInterval(load, 15000);
    return () => { alive = false; clearInterval(t); };
  }, [id]);

  const stepIdx = order ? STEPS.findIndex((s) => s.id === (order.tracking_stage || "Pendente")) : -1;
  const cancelado = order?.status === "Cancelado";
  const pago = order && !cancelado && ["Pago", "Enviado", "Entregue"].includes(order.status);

  return (
    <div data-testid="vult-order-page" className="min-h-screen bg-white text-slate-900">
      <header className="border-b border-slate-200 px-6 py-5">
        <Link to="/" data-testid="vult-order-home-link" className="font-display text-2xl font-black uppercase tracking-tight">
          VULT<span className="text-vult-red">.</span>
        </Link>
      </header>

      <main className="mx-auto max-w-2xl px-6 py-10">
        {error && (
          <div data-testid="vult-order-error" className="rounded-2xl border border-red-200 bg-red-50 p-6 text-center">
            <XCircle size={36} className="mx-auto text-red-500" />
            <p className="mt-3 font-display text-xl font-black uppercase">Pedido não encontrado</p>
            <p className="mt-1 text-sm text-slate-600">{error}</p>
          </div>
        )}

        {!order && !error && (
          <div className="flex items-center justify-center gap-3 py-20 text-slate-500">
            <Loader2 size={22} className="animate-spin" />
            <span className="font-mono text-xs font-bold uppercase tracking-widest">Carregando pedido...</span>
          </div>
        )}

        {order && (
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
            <p className="font-mono text-xs font-bold uppercase tracking-widest text-slate-500">Olá, {order.customer_name.split(" ")[0]}!</p>
            <h1 className="mt-1 font-display text-4xl font-black uppercase">Pedido {order.id}</h1>

            {cancelado ? (
              <div data-testid="vult-order-status-banner" className="mt-6 flex items-center gap-3 rounded-2xl border border-red-200 bg-red-50 p-5">
                <XCircle size={28} className="shrink-0 text-red-500" />
                <div>
                  <p className="font-display text-lg font-black uppercase text-red-600">Pedido cancelado</p>
                  <p className="text-sm text-slate-600">Fale com a loja no WhatsApp se tiver dúvidas.</p>
                </div>
              </div>
            ) : pago ? (
              <div data-testid="vult-order-status-banner" className="mt-6 flex items-center gap-3 rounded-2xl border border-emerald-200 bg-emerald-50 p-5">
                <CheckCircle2 size={28} className="shrink-0 text-emerald-600" />
                <div>
                  <p className="font-display text-lg font-black uppercase text-emerald-700">Pagamento confirmado!</p>
                  <p className="text-sm text-slate-600">Recebemos seu PIX. Agora estamos preparando seu pedido.</p>
                </div>
              </div>
            ) : (
              <div data-testid="vult-order-status-banner" className="mt-6 flex items-center gap-3 rounded-2xl border border-amber-200 bg-amber-50 p-5">
                <Loader2 size={28} className="shrink-0 animate-spin text-amber-500" />
                <div>
                  <p className="font-display text-lg font-black uppercase text-amber-700">Aguardando pagamento</p>
                  <p className="text-sm text-slate-600">Assim que o PIX for confirmado, esta página atualiza sozinha.</p>
                </div>
              </div>
            )}

            {!cancelado && (
              <div className="mt-8 space-y-1">
                {STEPS.map((s, i) => {
                  const done = i <= stepIdx;
                  const atual = i === stepIdx;
                  return (
                    <div key={s.id} data-testid={`vult-order-step-${s.id.toLowerCase()}`} className="flex items-center gap-4 py-2">
                      <div className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-full border-2 ${done ? "border-slate-900 bg-vult-lime" : "border-slate-200 bg-white text-slate-300"}`}>
                        <s.icon size={17} className={done ? "text-slate-900" : ""} />
                      </div>
                      <div className="flex-1">
                        <p className={`font-mono text-xs font-bold uppercase tracking-widest ${done ? "text-slate-900" : "text-slate-400"}`}>{s.label}</p>
                      </div>
                      {atual && <span className="rounded-full bg-slate-900 px-3 py-1 font-mono text-[9px] font-bold uppercase tracking-widest text-white">Agora</span>}
                      {done && !atual && <CheckCircle2 size={16} className="text-emerald-500" />}
                    </div>
                  );
                })}
              </div>
            )}

            <div className="mt-8 space-y-2 rounded-2xl bg-[#F8F9FA] p-5 font-mono text-xs">
              <div className="flex justify-between"><span className="text-slate-500">Código de rastreio</span><span data-testid="vult-order-tracking-code" className="font-bold text-vult-blue">{order.tracking_code}</span></div>
              <div className="flex justify-between"><span className="text-slate-500">Total</span><span data-testid="vult-order-total" className="font-bold">{formatBRL(order.total)}</span></div>
              {(order.items || []).map((it, i) => (
                <div key={i} className="flex justify-between gap-3 border-t border-slate-200 pt-2">
                  <span className="text-slate-500">{it.qty}x {it.name}{it.size ? ` (BR ${it.size})` : ""}</span>
                  <span className="font-bold">{formatBRL(it.price * it.qty)}</span>
                </div>
              ))}
            </div>

            {order.carrier_code && (
              <div data-testid="vult-order-carrier" className="mt-4 rounded-2xl border-2 border-slate-900 p-5">
                <p className="font-mono text-[10px] font-bold uppercase tracking-widest text-slate-500">Rastreio oficial dos Correios</p>
                <div className="mt-2 flex flex-wrap items-center gap-3">
                  <span className="font-display text-2xl font-black uppercase">{order.carrier_code}</span>
                  <button
                    data-testid="vult-order-carrier-copy"
                    onClick={() => { navigator.clipboard?.writeText(order.carrier_code); toast.success("Código copiado"); }}
                    className="flex items-center gap-1.5 rounded-full border border-slate-900 px-3 py-1.5 font-mono text-[10px] font-bold uppercase tracking-widest transition-colors hover:bg-slate-900 hover:text-white"
                  >
                    <Copy size={12} /> Copiar
                  </button>
                  <a
                    data-testid="vult-order-carrier-link"
                    href={`https://rastreamento.correios.com.br/app/index.php?objeto=${order.carrier_code}`}
                    target="_blank"
                    rel="noreferrer"
                    className="flex items-center gap-1.5 rounded-full bg-slate-900 px-3 py-1.5 font-mono text-[10px] font-bold uppercase tracking-widest text-white transition-colors hover:bg-vult-red hover:text-slate-900"
                  >
                    <ExternalLink size={12} /> Rastrear nos Correios
                  </a>
                </div>
              </div>
            )}

            <p className="mt-4 inline-block rounded-full bg-vult-lime px-4 py-2 font-mono text-[11px] font-bold uppercase tracking-widest">
              Prazo estimado: 20 dias até o destino final
            </p>

            <Link to="/" className="mt-8 flex items-center gap-2 font-mono text-xs font-bold uppercase tracking-widest text-slate-500 transition-colors hover:text-slate-900">
              <ArrowLeft size={14} /> Voltar para a loja
            </Link>
          </motion.div>
        )}
      </main>
    </div>
  );
}
